import { useState, useMemo } from "react";
import { FiUsers, FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";
import UserModal from "../UserModal";
import authAxios from "../../../api/authAxios";


export default function UsersTab({ org, currentUser, onAddUser, onEditUser, onDeleteUser }) {
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { theme } = useTheme();

  const role = (currentUser?.role || "").toLowerCase();
  const memberships = currentUser?.memberships || [];
  const orgRole =
    memberships.find((m) => m.organization?.id === org.id)?.role?.toLowerCase() || role;

  const globalRole = (currentUser?.global_role || "").toLowerCase();
  const isPlatformAdmin = globalRole === "platform_admin";
  const isOrgAdmin = orgRole === "org_admin" || currentUser?.global_role === "platform_admin";
  const isOrgUser = orgRole === "org_user" || orgRole === "user";

  const filteredMembers = useMemo(() => {
    if (!org?.members) return [];
    if (isOrgUser) return org.members.filter((m) => m.user.id === currentUser.id);
    return org.members.filter((m) => m.role?.toLowerCase() !== "platform_admin");
  }, [org, currentUser, isOrgUser]);

  const cardBase =
    theme === "dark"
      ? "bg-neutral-750 border border-neutral-700 text-slate-100"
      : "bg-white border border-gray-200 text-gray-900";


  const subText = theme === "dark" ? "text-gray-400" : "text-gray-500";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${cardBase} rounded-xl p-5 shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Users</h1>
            <p className={`${subText} text-sm mt-1`}>
              Manage users for <span className="text-blue-600">{org?.name || "Organization"}</span>.
            </p>
          </div>
          {isOrgAdmin && (
            <button
              onClick={() => {
                setEditingUser(null);
                setShowUserModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm text-white shadow"
            >
              <FiPlus /> Add User
            </button>
          )}
        </div>
      </div>

      {/* User List */}
      {filteredMembers.length === 0 ? (
        <div className={`${cardBase} p-6 rounded-xl text-center ${subText}`}>No users yet.</div>
      ) : (
        <ul className="space-y-3">
          {filteredMembers.map((m) => {
            const canEdit = isOrgAdmin || (isOrgUser && m.user.id === currentUser.id);
            const canDelete = isOrgAdmin && m.user.id !== currentUser.id;

            return (
              <li
                key={m.user.id}
                className={`${cardBase} flex justify-between items-center rounded-xl px-4 py-3 hover:shadow-md transition`}
              >
                <div>
                  <p className="font-semibold flex items-center gap-2">
                    <FiUsers className="text-purple-500" />
                    {m.user.name || m.user.email}
                  </p>
                  <p className={`text-xs ${subText}`}>Role: {m.role}</p>
                </div>

                {(canEdit || canDelete) && (
                  <div className="flex gap-3">
                    {canEdit && (
                      <button

onClick={async () => {
  const res = await authAxios.get(
    `/users/${m.user.id}/?organization=${org.id}`
  );
  setEditingUser(res.data);
  setShowUserModal(true);
}}

                        className="text-yellow-500 hover:text-yellow-600"
                      >
                        <FiEdit />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => onDeleteUser(org.id, m.user.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal */}
      <UserModal
        open={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setEditingUser(null);
        }}
        mode={editingUser ? "edit" : "add"}
        initialUser={editingUser}
        scope="org"
        currentUser={currentUser}
        organization={org}
        onSubmit={async (payload) => {
          if (editingUser) await onEditUser(org.id, payload);
          else await onAddUser(org.id, payload);
          setShowUserModal(false);
          setEditingUser(null);
        }}
        dark={theme === "dark"}
      />
    </div>
  );
}

